/* ───────────────────────────────────────────────────────────────────────────
   O16 CHECKOUT SHIELD — owner reversed the earlier decision (2026-09-08): the
   express-checkout wallet buttons (Shop Pay / PayPal / Google Pay) must stay
   VISIBLE for extended-delivery items, not hidden, and a tap on them must be
   caught and routed through the SAME consent modal as Add to cart, never a
   parallel one.

   THE PROBLEM: those buttons render inside <shopify-accelerated-checkout>
   (PDP) / <shopify-accelerated-checkout-cart> (cart drawer), which use a
   CLOSED shadow DOM — confirmed empirically 2026-09-08 (DEV theme
   186575225161, Playwright/Chromium): `document.querySelector(
   'shopify-accelerated-checkout').shadowRoot` and the same on its own child
   `shop-pay-wallet-button` both return null from outside. A closed root
   means we can never attach a listener to anything inside it, never read
   its content, never know when it re-renders. There is nothing to hook.

   THE FIX: a transparent <o16-checkout-shield> element laid on top of the
   button's own container (position:absolute; inset:0 — see
   assets/o16-extended-delivery-modal.css), inside a position:relative
   parent that holds NOTHING ELSE but the payment button markup. Because
   that box is pure CSS layout (no JS measuring / ResizeObserver), the
   shield always matches the button's own rendered box, at any size, on any
   resize — normal flow, not a snapshot.

   Two render sites share this ONE file (never duplicated):
     - snippets/main-product-section.liquid — data-o16-shield-mode="pdp".
       Info comes from the ancestor <product-form>'s OWN
       data-o16-extended-date / data-o16-extended-type attributes — the
       EXACT SAME attributes assets/o16-extended-delivery-consent.js already
       keeps live on every variant switch for the add-to-cart gate. This
       file adds no new data plumbing for the PDP case; it only reads what
       already exists.
     - snippets/cart-drawer.liquid AND sections/main-cart-items.liquid
       (cart PAGE, added 2026-09-08 for drawer/page parity — see that
       section's own header comment) — both data-o16-shield-mode="cart".
       Info comes from an <o16-dispatch-countdown data-pb-cart-in-cart
       data-pb-cart-consent data-pb-cart-type data-pb-cart-date> (rendered
       by snippets/o16-cart-reassurance.liquid, included by BOTH render
       sites), read via the SAME query assets/o16-preback-sync.js already
       uses elsewhere in this theme, with a drawer-scoped-first / unscoped-
       fallback pair (`document.querySelector('cart-drawer
       [data-pb-cart-in-cart]') || document.querySelector('[data-pb-cart-in-
       cart]')`) — the drawer and the cart page are mutually exclusive on
       any single page (the drawer never renders on request.page_type ==
       'cart'), so the fallback can never double-match.
       data-pb-cart-consent is the cart.attributes server-truth signal
       (independent of this browser's own localStorage) — needed because an
       item can enter the cart by a route that never touched the add-to-cart
       gate at all (a /cart permalink, a direct POST).

   ── THE HONEST LIMITATION (owner's own framing, 2026-09-07/08) ───────────
   A browser will only open a real payment sheet (Apple Pay / Google Pay) or
   follow a wallet's own navigation (Shop Pay) in direct response to a
   genuine, freshly-activated user gesture on that exact control. The tap
   this shield intercepts is spent on OPENING THE MODAL, not on the wallet
   button — so the wallet's own action can only ever be RE-TRIGGERED by a
   later, separate, still-genuine gesture: the customer's own tap on the
   modal's Confirm button. This file makes that re-trigger automatic (see
   retryTap below) using every technique that can survive that handoff, but
   because the wallet button lives behind a closed shadow DOM we can never
   confirm success from outside it (no event, no attribute, nothing crosses
   that boundary back to us) — so the calm follow-up line
   (.o16-checkout-shield-followup) is ALWAYS shown right after, never
   conditionally, as the honest fallback for whichever wallet type or
   browser the automatic re-trigger does not reach.

   MEASURED 2026-09-08 (DEV theme, Chromium via Playwright, this store's
   Shop Pay integration): the wallet SDK's own `eligibleToShow()` (visible
   in the unminified strings of
   https://o16.dk/cdn/shopifycloud/portable-wallets/latest/portable-wallets.en.js)
   reported "ineligible" in every automated session tried (fresh profile,
   navigator.webdriver both left true and spoofed to undefined) — Shop Pay
   renders NOTHING (0×0, no error) for a browser it does not recognise as a
   real prior Shop Pay buyer, which made it impossible to observe a real
   payment sheet open/not-open from this environment. What IS verified
   directly, independent of that SDK's eligibility state:
     - <shopify-accelerated-checkout> and <shop-pay-wallet-button> both
       report shadowRoot === null from outside (closed).
     - The "More payment options" fallback link
       (#more-payment-options-link, class shopify-payment-button__more-options)
       is a PLAIN, un-shadowed light-DOM <a> — a direct child of the light
       DOM, reachable by an ordinary querySelector with no shadow boundary
       at all. A .click() on it is not a "best effort", it is a completely
       ordinary DOM call.
   Given that, retryTap() below always tries the ordinary/light-DOM route
   first (guaranteed to fire) and treats the closed-shadow hosts themselves
   as best-effort only. Reverse-engineering Shop Pay's private navigation
   URL scheme from the wallet-params/access-token attributes it exposes was
   considered and rejected: it is an undocumented, internal Shopify
   contract with no public API, exactly the kind of guessed surface this
   project's own rules forbid shipping.

   ── THE CHECKOUT-BUTTON GATE (owner escalation 2026-09-08, code review) ──
   A code review caught what the "gating the ADD means there is nothing
   left for a wallet button to bypass" premise above quietly assumed away:
   the ORDINARY Checkout submit button was never gated at all. It has no
   shadow boundary and no app/SDK sitting in front of it, so — unlike the
   wallets — nothing about it stops a bare .click() from being genuinely
   reliable. Both render sites now wrap it the same way they wrap the
   wallet container (a `data-o16-shield-mode="cart"` instance over
   `.o16-checkout-btn-wrap`, see snippets/cart-drawer.liquid +
   sections/main-cart-items.liquid), reusing this exact class with no new
   code path — _sync()/readCartInfo()/_begin()/retryTap() are unmodified;
   only releaseGatedButton() below exists specifically for this case (see
   its own header comment for why the Liquid-rendered `disabled` attribute,
   the no-JS/ad-blocker backstop, has to be cleared the moment this shield
   goes live, or retryTap()'s own .click() on it would be a silent no-op).
   Because retryTap() is fully reliable for this button, no
   `data-o16-shield-followup` note is rendered for the checkout-button
   shield group — see the two render sites' own comments.
─────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  function o16(name) {
    return window.O16ExtendedDelivery && window.O16ExtendedDelivery[name];
  }

  function consentAlreadyGiven() {
    var hasConsent = o16('hasConsent');
    return !!(hasConsent && hasConsent());
  }

  // "BACK-ORDER" (raw upcased map value) -> "Back order", same transform as
  // assets/o16-extended-delivery-consent.js's own humanizeType — duplicated
  // as a tiny pure function rather than exported, since it is one line and
  // exporting it would widen that file's public surface for no reason.
  function humanizeType(type) {
    if (!type) return '';
    // Lowercase, same as o16-extended-delivery-consent.js's humanizeType and the
    // Liquid side's | downcase (2026-09-16): the modal reads "This item is a pre order."
    return type.replace(/-/g, ' ').toLowerCase();
  }

  function readPdpInfo(shieldEl) {
    var form = shieldEl.closest('product-form');
    if (!form) return null;
    var date = (form.dataset.o16ExtendedDate || '').trim();
    var type = (form.dataset.o16ExtendedType || '').trim();
    if (!date || !type) return null;
    return { extended: true, date: date, type: type };
  }

  // Same query assets/o16-preback-sync.js already uses to read the cart
  // drawer's own aggregate pre/back-order state — not a new convention.
  // O16 CHECKOUT SHIELD cart-PAGE parity (owner escalation, 2026-09-08):
  // the cart PAGE (sections/main-cart-items.liquid) renders its own copy of
  // <o16-dispatch-countdown data-pb-cart-*> via the SAME
  // snippets/o16-cart-reassurance.liquid include the drawer uses, but with
  // no <cart-drawer> ancestor at all — the drawer is never rendered on
  // request.page_type == 'cart' (see that section's own header comment).
  // Same fallback idiom this theme already uses for the identical problem
  // (assets/o16-cart-drawer.js's o16PatchDiscountTotals:
  // `document.querySelector('#CartDrawer .o16-totals-wrap') ||
  // document.querySelector('.o16-totals-wrap')`) — try the drawer-scoped
  // query first, fall back to the page's own unscoped copy. The two are
  // mutually exclusive on any single page, so this can never double-match.
  function readCartInfo() {
    var el =
      document.querySelector('cart-drawer [data-pb-cart-in-cart]') ||
      document.querySelector('[data-pb-cart-in-cart]');
    if (!el) return null;
    if (el.getAttribute('data-pb-cart-in-cart') !== 'true') return null;
    if (el.getAttribute('data-pb-cart-consent') === 'true') return null;
    // TEN-DAY THRESHOLD (owner, 2026-09-08) — the cart aggregate's own gate
    // (snippets/o16-preback-cart-status.liquid, computed once on the
    // "latest date wins" winner across every line, never per line — see
    // that file's own PASS 1/2 header). "false" means the whole cart is
    // within the threshold: no shield, no disabled Checkout, no wallet
    // interception at all. Missing attribute defaults to gate-required
    // (fail toward the existing, tested behaviour), same direction as
    // every other blank-date default in this feature.
    if (el.getAttribute('data-pb-cart-gate') === 'false') return null;
    var date = el.getAttribute('data-pb-cart-date') || '';
    var type = humanizeType(el.getAttribute('data-pb-cart-type') || '');
    if (!date || !type) return null;
    return { extended: true, date: date, type: type };
  }

  function fetchCurrentCartAttributes() {
    var cartRoutes = o16('cartRoutes');
    if (!cartRoutes) return Promise.resolve({});
    var routes = cartRoutes();
    return fetch(routes.cartJson, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        return (cart && cart.attributes) || {};
      })
      .catch(function () {
        return {};
      });
  }

  // Fire-and-forget the cart-attribute stamp via navigator.sendBeacon —
  // deliberately NOT fetch(). Two independent reasons, both fatal if this
  // is ever "simplified" back to fetch:
  //   1. ACTIVATION: a fetch here would not itself block retryTap() below
  //      (that call is already ordered first), but the moment this file's
  //      onConfirmSync callback is done running, the browser may treat the
  //      Confirm click's user activation as spent (Chromium: transient
  //      activation is a short, consumable window). sendBeacon queues the
  //      request instantly and returns — it never awaits, so it can never
  //      be the thing that stretches this callback across a task boundary.
  //   2. SURVIVAL: retryTap() may hand control to a real navigation (Shop
  //      Pay typically navigates the current tab). A pending fetch would be
  //      aborted by that navigation before its response ever arrives;
  //      sendBeacon is specifically designed to survive page unload.
  function stampConsentBeacon(existingAttributes, grantedISO) {
    var attributeName = o16('attributeName');
    var cartRoutes = o16('cartRoutes');
    if (!attributeName || !cartRoutes || !navigator.sendBeacon) return;
    var attributes = {};
    Object.keys(existingAttributes || {}).forEach(function (key) {
      attributes[key] = existingAttributes[key];
    });
    attributes[attributeName] = 'Yes, consent given ' + grantedISO;
    try {
      var blob = new Blob([JSON.stringify({ attributes: attributes })], {
        type: 'application/json'
      });
      navigator.sendBeacon(cartRoutes().update, blob);
    } catch (e) {
      // Best effort only — a failed stamp never blocks the retrigger above.
    }
  }

  // Re-fires the tap the shield intercepted, now that consent is confirmed.
  // MUST run synchronously inside the Confirm button's own click handler
  // (see how onConfirmSync is wired below) — see this file's header for the
  // full activation chain this protects.
  function retryTap(container) {
    if (!container) return;

    // Ordinary, un-shadowed light-DOM controls first — see the header
    // comment's "MEASURED 2026-09-08" note. Real DOM calls, not a guess.
    var plain = container.querySelectorAll('a, button, [role="button"]');
    plain.forEach(function (el) {
      try {
        el.click();
      } catch (e) {
        /* best effort */
      }
    });

    // Closed-shadow wallet hosts — best effort only (see header comment).
    // Dispatching a composed, bubbling click AT the host is a plain DOM
    // guarantee (it always reaches the host's OWN listeners); whether
    // Shopify's component happens to bind its click handling to the host
    // itself, versus exclusively to an internal node only a real pointer
    // hit-test can reach, is proprietary and unverifiable from here.
    var hosts = container.querySelectorAll(
      'shopify-accelerated-checkout, shopify-accelerated-checkout-cart, shop-pay-wallet-button'
    );
    hosts.forEach(function (host) {
      try {
        var opts = { bubbles: true, cancelable: true, composed: true, view: window };
        if (typeof host.click === 'function') host.click();
        host.dispatchEvent(new MouseEvent('click', opts));
        if (typeof PointerEvent === 'function') {
          host.dispatchEvent(new PointerEvent('pointerdown', opts));
          host.dispatchEvent(new PointerEvent('pointerup', opts));
        }
      } catch (e) {
        /* best effort only — see file header */
      }
    });
  }

  function showFollowup(shieldEl) {
    var group = shieldEl.closest('[data-o16-shield-group]');
    var note = group && group.querySelector('[data-o16-shield-followup]');
    if (note) note.hidden = false;
  }

  // O16 CHECKOUT-BUTTON GATE (owner escalation 2026-09-08, code review) —
  // the ordinary Checkout submit button (snippets/cart-drawer.liquid +
  // sections/main-cart-items.liquid) is now ALSO server-rendered `disabled`
  // whenever the cart holds an extended-delivery item without recorded
  // consent (the Liquid-computed backstop for when this very script never
  // runs at all — an ad-blocker, a CSP). Once this script DOES run and this
  // shield goes live over that same button, the `disabled` attribute is
  // pure liability: retryTap() below re-fires a tap on this exact button by
  // calling .click() on it directly, and .click() on a disabled element is
  // a guaranteed no-op (per the HTML spec, a disabled control never
  // dispatches a click or runs its default action) — so without this, a
  // customer who taps the shield, reads the modal, and confirms would land
  // right back on a dead button with no visible error. The shield itself
  // (a separate, absolutely-positioned layer on top, with its own capture-
  // phase listeners) is what actually blocks a premature tap regardless of
  // whether the real button underneath is enabled or disabled — so
  // releasing `disabled` here does not reopen the gate, it just makes the
  // JS layer's own later auto-continue possible. Scoped to the shield's own
  // container (never a global lookup): a no-op for the wallet shield, which
  // has no disabled button in its container to begin with.
  function releaseGatedButton(container) {
    var btn = container && container.querySelector('button[disabled]');
    if (btn) btn.disabled = false;
  }

  // Once ANY shield (PDP or cart) records a fresh yes, every shield on the
  // page must disappear immediately — the underlying consent is global and
  // permanent (assets/o16-extended-delivery-consent.js's own "ONE YES,
  // NEVER AGAIN" policy), and the PDP section renders twice (desktop +
  // mobile wrappers, a known condition of this theme), so there are always
  // at least two <o16-checkout-shield> instances to update in lockstep.
  function hideAllShields() {
    document.querySelectorAll('o16-checkout-shield').forEach(function (el) {
      el._consented = true;
      el.hidden = true;
    });
  }

  function defineShield() {
    if (customElements.get('o16-checkout-shield')) return;

    customElements.define(
      'o16-checkout-shield',
      class extends HTMLElement {
        connectedCallback() {
          this._mode = this.getAttribute('data-o16-shield-mode') || 'pdp';
          this._handled = false;
          this._onTap = this._onTap.bind(this);
          this.addEventListener('pointerdown', this._onTap, true);
          this.addEventListener('click', this._onTap, true);
          this._sync();
        }

        disconnectedCallback() {
          this.removeEventListener('pointerdown', this._onTap, true);
          this.removeEventListener('click', this._onTap, true);
        }

        // Visibility:
        //  - PDP: CSS alone shows/hides this element, keyed off the
        //    ancestor <product-form>'s data-o16-extended-date attribute
        //    (assets/o16-extended-delivery-modal.css) — the exact same
        //    attribute assets/o16-extended-delivery-consent.js already
        //    keeps live on every variant switch, so no JS listener is
        //    needed here to react to a variant change. This method only
        //    ever needs to force-hide once consent is already remembered
        //    (localStorage is the PDP add-to-cart gate's own signal — see
        //    guard() in assets/o16-extended-delivery-consent.js).
        //  - cart: no CSS shortcut is possible (the signal lives on a
        //    sibling section, <o16-dispatch-countdown>, not an ancestor of
        //    this element), so visibility is fully JS-driven, read fresh
        //    every time this element (re)connects — which happens on every
        //    drawer re-render (Section Rendering API), so it is always
        //    current with the cart's real contents.
        _sync() {
          if (this._mode === 'cart') {
            var info = readCartInfo();
            this.hidden = !info;
            // See releaseGatedButton's own header — only relevant the
            // moment this shield is actually going live (info truthy); a
            // no-op otherwise, and a no-op for the wallet shield's
            // container regardless (no disabled button there).
            if (info) releaseGatedButton(this.parentElement);
            return;
          }
          if (consentAlreadyGiven()) {
            this._consented = true;
            this.hidden = true;
          }
        }

        _onTap(event) {
          if (this.hidden || this._consented) return;
          // Belt and suspenders on both event types that can reach this
          // element (owner's explicit instruction) — capture phase so this
          // always wins regardless of any other delegated listener nearer
          // the wallet container.
          event.preventDefault();
          event.stopPropagation();
          if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
          }
          // pointerdown and click both arrive for a single physical tap;
          // only act once per tap. Not part of the activation-critical
          // path below (that begins fresh at the modal's OWN Confirm
          // click, a separate later gesture) — this is purely a
          // double-handling guard.
          if (this._handled) return;
          this._handled = true;
          var self = this;
          setTimeout(function () {
            self._handled = false;
          }, 500);
          this._begin();
        }

        _begin() {
          var info = this._mode === 'cart' ? readCartInfo() : readPdpInfo(this);
          if (!info) {
            this._sync();
            return;
          }
          // "Confirm and add to cart" (the modal's default wording) is
          // simply wrong here — nothing is being added to a cart, the PDP
          // wallet buys the selected variant directly. See
          // assets/o16-extended-delivery-consent.js's own request() for how
          // this overrides the button text only for this call.
          info.confirmLabel = 'Confirm and continue';
          var modal = document.querySelector('o16-extended-modal');
          if (!modal || typeof modal.request !== 'function') return;

          var container = this.parentElement;
          var self = this;
          // Kicked off immediately, in parallel with the customer actually
          // reading the modal — by the time Confirm is tapped seconds
          // later this has almost always already resolved, so the
          // synchronous callback below never waits on it (see
          // stampConsentBeacon's own header for why that call itself still
          // uses sendBeacon rather than depending on this timing).
          var attrPromise = fetchCurrentCartAttributes();

          modal.request(info, function onConfirmSync() {
            var grantedISO = new Date().toISOString();
            var rememberConsent = o16('rememberConsent');
            if (rememberConsent) rememberConsent(grantedISO);

            attrPromise.then(function (existing) {
              stampConsentBeacon(existing, grantedISO);
            });

            hideAllShields();
            retryTap(container);
            showFollowup(self);
          });
        }
      }
    );
  }

  defineShield();
})();
